import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, Send, Archive, User } from "lucide-react";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export default function Comments({ comentarios, onAddComment, onArchiveComment }) {
  const [nuevoComentario, setNuevoComentario] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!nuevoComentario.trim()) return;
    
    onAddComment(nuevoComentario);
    setNuevoComentario("");
  };

  const comentariosActivos = comentarios.filter(c => !c.archivado);
  const comentariosArchivados = comentarios.filter(c => c.archivado);

  const getDisplayName = (email) => {
    if (!email) return "Usuario";
    // Extraer nombre del email: nombre.apellido@domain.com -> Nombre Apellido
    const nombrePart = email.split('@')[0];
    return nombrePart
      .split(/[._-]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2 text-gray-900 dark:text-white">
          <MessageSquare className="w-5 h-5" />
          Comentarios Internos
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="mb-6">
          <Textarea
            value={nuevoComentario}
            onChange={(e) => setNuevoComentario(e.target.value)}
            placeholder="Escribe un comentario interno..."
            rows={3}
            className="mb-3 dark:bg-gray-800 dark:border-gray-700"
          />
          <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700">
            <Send className="w-4 h-4 mr-2" />
            Añadir Comentario
          </Button>
        </form>

        <div className="space-y-4">
          {comentariosActivos.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">Sin comentarios</p>
          ) : (
            comentariosActivos.slice().reverse().map((comentario, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-indigo-500 text-white flex items-center justify-center">
                      <User className="w-4 h-4" />
                    </div>
                    <p className="font-medium text-sm text-gray-900 dark:text-white">
                      {getDisplayName(comentario.usuario)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {format(new Date(comentario.fecha), "dd MMM yyyy, HH:mm", { locale: es })}
                    </p>
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      className="h-6 w-6 text-gray-400 hover:text-gray-600"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onArchiveComment(comentario);
                      }}
                      title="Archivar comentario"
                    >
                      <Archive className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 ml-9">{comentario.texto}</p>
              </div>
            ))
          )}
        </div>

        {comentariosArchivados.length > 0 && (
          <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowArchived(!showArchived)}
              className="text-gray-500 text-xs"
            >
              <Archive className="w-3 h-3 mr-1" />
              {showArchived ? "Ocultar" : "Ver"} archivados ({comentariosArchivados.length})
            </Button>
            
            {showArchived && (
              <div className="space-y-3 mt-3">
                {comentariosArchivados.slice().reverse().map((comentario, index) => (
                  <div key={index} className="bg-gray-100 dark:bg-gray-800/50 p-3 rounded-lg opacity-60">
                    <div className="flex justify-between items-start mb-1">
                      <p className="font-medium text-xs text-gray-600 dark:text-gray-400">
                        {getDisplayName(comentario.usuario)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {format(new Date(comentario.fecha), "dd MMM yyyy", { locale: es })}
                      </p>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{comentario.texto}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}